import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Add,
  Edit,
  ArrowDropUp,
  ArrowDropDown,
  BarChart,
  Close,
} from '@mui/icons-material';
import {
  Paper,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  Skeleton,
  Backdrop,
  Fab,
  Tooltip,
  TooltipProps,
  tooltipClasses,
} from '@mui/material';
import { ModalLink } from '@scorenco/components';
import {
  Types,
  useDeleteChallengePhaseOne,
  useDeleteCupOrPool,
  useTranslation,
  useUpdatePhasesOrder,
} from '@scorenco/core';
import { NextPageWithLayout } from '@scorenco/next';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';

import { T_KEYS } from '@/translations';

import { DeleteModalButton } from '../../../../../../components';

import { AddCupModal } from './_internal/AddCupModal';
import { AddPoolModal } from './_internal/AddPoolModal';
import { AddRulesModal } from './_internal/AddRulesModal';
import { ChallengeLayout } from './_internal/ChallengeLayout';
import { EditRulesModal } from './_internal/EditRulesModal';
import { GetRulesDocument } from './_internal/index.query.generated';
import { getGameTypes } from './_internal/Phase/_internal/getGameTypes';
import { GroupTeams } from './_internal/Phase/_internal/GroupTeams';
import { PhaseName } from './_internal/PhaseName';
import { TeamEngagementChoice } from './_internal/TeamEngagementChoice';

const CupPhase = ({ phase, challengeTeams }) => {
  const { t } = useTranslation();

  const steps = useMemo(() => {
    return Array(5)
      .fill()
      .map((_, i) => ({
        stepNumber: i + 1,
        games: getGameTypes(i + 1).map((gameType) =>
          phase.games.filter((game) => game.game.game_type === gameType)
        ),
      }))
      .filter((step) => step.games[0]?.length > 0)
      .reverse();
  }, [phase.games]);

  return (
    <Box
      key={phase.id}
      sx={{
        padding: 0,
        paddingLeft: 2,
        paddingRight: 2,
        paddingY: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '0.5rem',
        width: '100%',
      }}
    >
      <PhaseName phase={phase} sx={{ mx: 'auto', mb: 2 }} />
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{
          width: '100%',
        }}
      >
        {steps.map((step, index) => {
          return (
            <>
              {index > 0 && (
                <Divider orientation="vertical" flexItem variant="fullWidth" />
              )}
              <Stack
                key={step.stepNumber}
                direction="column"
                alignItems="flex-start"
                justifyContent="flex-start"
                gap={2}
              >
                <Chip
                  size="small"
                  color="primary"
                  label={
                    (step.stepNumber === 1 &&
                      t(T_KEYS.TOURNAMENTS_FINALES_LABEL)) ||
                    (step.stepNumber === 2 &&
                      t(T_KEYS.TOURNAMENTS_SEMI_FINALS_LABEL)) ||
                    (step.stepNumber === 3 &&
                      t(T_KEYS.TOURNAMENTS_QUARTER_FINALS_LABEL)) ||
                    (step.stepNumber === 5 &&
                      t(T_KEYS.TOURNAMENTS_ROUND_OF_SIXTEEN_LABEL))
                  }
                />
                <Box sx={{ width: '100%' }}>
                  {step.games.map((games) =>
                    games.map((g) => {
                      if (index === 0 && phase?.rule?.rank === 1) {
                        return (
                          <Paper
                            variant="bordered"
                            key={step.stepNumber}
                            sx={{ mb: 2, p: 2 }}
                          >
                            <GroupTeams
                              phase={phase}
                              engagements={[
                                g.game.home_engagement,
                                g.game.away_engagement,
                              ]}
                              challengeTeams={challengeTeams}
                              gameName={g.game.name}
                            />
                          </Paper>
                        );
                      } else {
                        return (
                          <Paper
                            variant="bordered"
                            key={g.id}
                            sx={{
                              padding: '1rem 1rem',
                              paddingTop: '0.5rem',
                              alignItems: 'flex-start',
                              marginBottom: '1rem',

                              gap: '0.5rem',
                            }}
                          >
                            {g.game.name}
                            <TeamEngagementChoice
                              engagement={g.game.home_engagement}
                              phase={phase}
                            />
                            <TeamEngagementChoice
                              engagement={g.game.away_engagement}
                              phase={phase}
                            />
                          </Paper>
                        );
                      }
                    })
                  )}
                </Box>
              </Stack>
            </>
          );
        })}
      </Stack>
    </Box>
  );
};

const AddChallengeLink = ({ challengeId, newRank }) => {
  const { t } = useTranslation();
  return (
    <ModalLink Modal={AddRulesModal} modalProps={{ challengeId, newRank }}>
      <Button variant="contained" color="secondary" startIcon={<Add />}>
        {t(T_KEYS.TOURNAMENTS_CHALLENGE_ADD_PHASE)}
      </Button>
    </ModalLink>
  );
};

const AddPoolLink = ({ ruleId, newRank }) => {
  return (
    <ModalLink Modal={AddPoolModal} modalProps={{ ruleId, newRank }}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<Add />}
      >
        Ajouter une nouvelle poule
      </Button>
    </ModalLink>
  );
};
const AddCupLink = ({ ruleId, newRank }) => {
  return (
    <ModalLink Modal={AddCupModal} modalProps={{ ruleId, newRank }}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<Add />}
      >
        Ajouter un nouveau tour de coupe
      </Button>
    </ModalLink>
  );
};

const EditChallengeLink = ({ rule, sportId }) => {
  return (
    <ModalLink Modal={EditRulesModal} modalProps={{ rule, sportId }}>
      <IconButton color="primary">
        <Edit />
      </IconButton>
    </ModalLink>
  );
};

const StylizedTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    maxWidth: 220,
    fontSize: '1.25rem',
    border: '1px solid #dadde9',
    borderColor: theme.palette.secondary.main,
    boxShadow: 'var(--snc-shadows-6)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
    '&:before': {
      border: '1px solid #dadde9',
      borderColor: theme.palette.secondary.main,
    },
  },
}));

const Phases = ({
  phases,
  rule,
  challengeTeams,
  isLast,
}: {
  phases: Types.Tournaments_Phase[];
  rule: Types.Tournaments_Challenge_Rule;
  challengeTeams: Types.Tournaments_Team[];
  isLast: boolean;
}) => {
  const [items, setItems] = useState<Types.Tournaments_Phase[]>([]);
  const deleteCupOrPoolMutation = useDeleteCupOrPool();
  const reorderPhasesMutation = useUpdatePhasesOrder();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (phases) {
      setItems(phases);
    }
  }, [phases]);

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newItems = [...items];
    const item = newItems[index];
    newItems[index] = newItems[index - 1];
    newItems[index - 1] = item;

    setItems(newItems);

    try {
      await reorderPhasesMutation.execute({
        updates: newItems.map((p) => ({
          where: {
            id: { _eq: p.id },
          },
          _set: {
            rank: newItems.findIndex((i) => i.id === p.id) + 1,
          },
        })),
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('An error occurred', { variant: 'error' });
    }
  };

  const handleMoveDown = async (index) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    const item = newItems[index];
    newItems[index] = newItems[index + 1];
    newItems[index + 1] = item;

    setItems(newItems);

    try {
      await reorderPhasesMutation.execute({
        updates: newItems.map((p) => ({
          where: {
            id: { _eq: p.id },
          },
          _set: {
            rank: newItems.findIndex((i) => i.id === p.id) + 1,
          },
        })),
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('An error occurred', { variant: 'error' });
    }
  };
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen(!open);
  };

  const engagmentsCountInPhases = isLast
    ? items?.map((phase) =>
        phase?.display_type === 'pool'
          ? phase?.engagements?.length
          : phase.games?.filter(
              (g) =>
                g.game?.game_type ===
                [...phase.games]?.sort(
                  (a, b) => a.game.game_type + b.game.game_type
                )[0].game.game_type
            )?.length * 2
      )
    : [];

  return (
    <>
      {isLast && (
        <>
          <Fab
            variant="extended"
            size="small"
            color="secondary"
            sx={{
              position: 'fixed',
              bottom: 75,
              right: 110,
              zIndex: open ? (theme) => theme.zIndex.drawer + 2 : '2',
            }}
            onClick={handleToggle}
          >
            {open ? <Close sx={{ mr: 1 }} /> : <BarChart sx={{ mr: 1 }} />}
            Comment est calculé le classement ?
          </Fab>
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              display: open ? 'flex' : 'none',
            }}
            open={open}
            onClick={handleToggle}
          />
        </>
      )}
      {items?.map((phase, index) => {
        const engagementsBeforeThisPhase = engagmentsCountInPhases
          ?.slice(0, index)
          .reduce((acc, val) => acc + val, 0);
        return (
          <StylizedTooltip
            key={phase.id}
            open={open}
            arrow
            placement="right"
            title={`Places de ${1 + engagementsBeforeThisPhase} à ${
              engagmentsCountInPhases?.[index] + engagementsBeforeThisPhase
            }`}
            sx={{
              '.${tooltipClasses.tooltip}': {
                backgroundColor: 'white !important',
              },
            }}
          >
            <Paper
              key={phase.id}
              variant="bordered"
              sx={{
                width: '100%',
                marginBottom: '2rem',
                zIndex: open ? (theme) => theme.zIndex.drawer + 2 : 'auto',
              }}
            >
              {rule.rule_type === 1 ||
              rule.rule_type === 5 ||
              rule.rule_type === 6 ||
              phase?.display_type === 'pool' ? (
                rule?.rank === 1 ? (
                  <GroupTeams
                    phase={phase}
                    engagements={phase?.engagements}
                    challengeTeams={challengeTeams}
                  />
                ) : (
                  <Box sx={{ m: '0', p: 2, width: '100%' }}>
                    <PhaseName phase={phase} />
                    {phase?.engagements?.map((engagement) => (
                      <Box sx={{ width: '100%', mb: 2 }} key={engagement.id}>
                        <TeamEngagementChoice
                          engagement={engagement}
                          phase={phase}
                        />
                      </Box>
                    ))}
                  </Box>
                )
              ) : (
                <CupPhase
                  key={phase.id}
                  phase={phase}
                  challengeTeams={challengeTeams}
                />
              )}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ width: '100%' }}
              >
                <StylizedTooltip
                  key={phase.id}
                  open={open}
                  arrow
                  placement="right"
                  title="Utilisez ces flèches pour réorganiser les groupes afin de calculer le bon classement général"
                  sx={{
                    '.${tooltipClasses.tooltip}': {
                      backgroundColor: 'white !important',
                    },
                  }}
                >
                  <Box>
                    <IconButton
                      disabled={index === 0}
                      onClick={() => handleMoveUp(index)}
                    >
                      <ArrowDropUp />
                    </IconButton>
                    <IconButton
                      disabled={index === items.length - 1}
                      onClick={() => handleMoveDown(index)}
                    >
                      <ArrowDropDown />
                    </IconButton>
                  </Box>
                </StylizedTooltip>
                <DeleteModalButton
                  onValidate={async () => {
                    await deleteCupOrPoolMutation.execute({
                      phaseId: phase.id,
                    });
                  }}
                  loading={deleteCupOrPoolMutation.status.loading}
                  message={t(T_KEYS.DELETE_CONFIRMATION, {
                    delete_confirmation_message: '"' + phase.name + '"',
                  })}
                  explanation={`Attention, la suppression de "${phase.name}" entrainera la suppression de toutes les rencontres associées.`}
                  sx={{ ml: 'auto' }}
                />
              </Stack>
            </Paper>
          </StylizedTooltip>
        );
      })}
    </>
  );
};

const ChallengeRules: NextPageWithLayout = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const deleteChallengePhaseMutation = useDeleteChallengePhaseOne();

  const getRules = useQuery(GetRulesDocument, {
    variables: {
      challengeId: Number(router.query.challengeId),
    },
    skip: !router.query.challengeId,
    context: {
      role: 'club',
    },
  });

  return (
    <Box sx={{ minHeight: '80vh' }}>
      <Stack spacing={3} alignItems="flex-start" direction="row">
        {getRules?.loading && (
          <Stack direction="column" gap={2}>
            <Skeleton variant="rectangular" height="9.5rem" width="20rem" />
            <Skeleton variant="rectangular" height="20rem" width="20rem" />
            <Skeleton variant="rectangular" height="20rem" width="20rem" />
          </Stack>
        )}
        {getRules.data?.tournaments_challenge_by_pk?.rules?.map(
          (rule, index) => {
            return (
              <>
                {index !== 0 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: '50rem' }}
                  />
                )}
                <Stack gap="1rem" alignItems="center">
                  <Paper key={index} variant="bordered" sx={{ width: '100%' }}>
                    <Stack
                      direction="column"
                      sx={{
                        width: '100%',
                      }}
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          component="span"
                          css={(theme) => css`
                            background: ${theme.vars.palette.primary.main};
                            padding: 0.5rem;
                            border-radius: 999rem;
                            width: 2rem;
                            height: 2rem;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: bold;
                            color: white;
                          `}
                        >
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography variant="h4">{rule.name}</Typography>
                        </Box>
                      </Stack>
                      <Stack flexDirection="row">
                        <EditChallengeLink
                          rule={rule}
                          sportId={
                            getRules.data?.tournaments_challenge_by_pk
                              ?.tournament?.sport_id
                          }
                        />
                        <DeleteModalButton
                          onValidate={async () => {
                            await deleteChallengePhaseMutation.execute({
                              ruleId: rule.id,
                            });
                          }}
                          loading={deleteChallengePhaseMutation.status.loading}
                          message={t(T_KEYS.DELETE_CONFIRMATION, {
                            delete_confirmation_message: '"' + rule.name + '"',
                          })}
                          explanation={`Attention, la suppression de "${rule.name}" entrainera la suppression de toutes les rencontres associées.`}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                  <Phases
                    phases={rule.phases}
                    rule={rule}
                    challengeTeams={
                      getRules.data?.tournaments_challenge_by_pk?.teams
                    }
                    isLast={
                      index ===
                      getRules.data?.tournaments_challenge_by_pk?.rules
                        ?.length -
                        1
                    }
                  />
                  <AddPoolLink
                    ruleId={rule.id}
                    newRank={rule.phases[rule.phases.length - 1]?.rank + 1 || 1}
                  />
                  <AddCupLink
                    ruleId={rule.id}
                    newRank={rule.phases[rule.phases.length - 1]?.rank + 1 || 1}
                  />
                </Stack>
              </>
            );
          }
        )}

        <Divider orientation="vertical" flexItem sx={{ height: '50rem' }} />
        <Paper variant="bordered">
          <AddChallengeLink
            challengeId={Number(router.query.challengeId)}
            newRank={
              getRules.data?.tournaments_challenge_by_pk?.rules[
                getRules.data?.tournaments_challenge_by_pk?.rules?.length - 1
              ]?.rank + 1 || 1
            }
          />
        </Paper>
      </Stack>
    </Box>
  );
};

ChallengeRules.getLayout = ChallengeLayout.buildGetLayout({
  navigation: {
    noAppBar: true,
    noBurgerDrawer: true,
  },
});

export default ChallengeRules;
